import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/objectives`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;


const objectivesSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "Objectives of SEVAA Foundation",
  alternateName: "SEVAA Foundation Mission, Vision and Objectives",
  url: pageUrl,
  inLanguage: ["en-IN", "hi-IN"],
  description:
    "Explore SEVAA Foundation's objectives for reducing poverty and sustaining dignity through education, healthcare, food security, elderly support and grassroots development for tribal and disadvantaged communities.",
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: logoImage,
    areaServed: [
      {
        "@type": "Country",
        name: "India",
      },
      {
        "@type": "Place",
        name: "Jharkhand",
      },
      {
        "@type": "Place",
        name: "Chatra",
      },
      {
        "@type": "Place",
        name: "Pakur",
      },
    ],
  },
  about: [
    "Poverty Reduction",
    "Tribal Community Support",
    "Rural Development",
    "Child Education",
    "Healthcare Support",
    "Food Security",
    "Hunger Relief",
    "Elderly Support",
    "Extreme Poverty Support",
    "Grassroots Social Work",
    "Community Led Development",
    "Self Reliance",
    "Charity Donation",
    "Volunteer Work",
  ],
  mainEntity: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: logoImage,
    slogan: "सेवा परमो धर्मः",
    description:
      "SEVAA Foundation is a people-centric nonprofit organization working to reduce poverty and sustain dignity by supporting tribal and extremely disadvantaged communities through education, healthcare and food security.",
    knowsAbout: [
      "Education NGO",
      "Healthcare NGO",
      "Food Security NGO",
      "Tribal Welfare",
      "Rural Development",
      "Elderly Support",
      "Poverty Reduction",
      "Grassroots NGO",
      "Social Welfare",
    ],
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${pageUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Objectives",
      item: pageUrl,
    },
  ],
};

const Objectives: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Objectives | SEVAA Foundation Tribal NGO</title>

        <meta
          name="title"
          content="Objectives | SEVAA Foundation Tribal NGO"
        />

        <meta
          name="description"
          content="Explore SEVAA Foundation objectives for tribal community support through education, healthcare, food security and poverty reduction programs."
        />

        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />

        <meta
          property="og:title"
          content="Objectives | SEVAA Foundation Tribal NGO"
        />

        <meta
          property="og:description"
          content="Know SEVAA Foundation's objectives for education, healthcare, food security, poverty reduction and dignity support for tribal and rural communities."
        />

        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content="SEVAA Foundation Objectives" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="Objectives | SEVAA Foundation NGO"
        />

        <meta
          name="twitter:description"
          content="SEVAA Foundation works for tribal communities through education, healthcare, food security, poverty reduction and grassroots support."
        />

        <meta name="twitter:image" content={defaultImage} />
        <meta
          name="twitter:image:alt"
          content="SEVAA Foundation objectives for tribal communities"
        />

        <script type="application/ld+json">
          {JSON.stringify(objectivesSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main className="bg-white font-poppins min-h-screen pb-10">
        {/* 1. HERO SECTION */}
        <section
          aria-labelledby="objectives-heading"
          className="relative w-full h-[400px] md:h-[500px]"
        >
          <img
            src="https://content.ckcjewellers.com/media/ckc/images/staticsImage/charityBanner.jpg"
            alt="SEVAA Foundation objectives for tribal education healthcare food security and poverty reduction"
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
          />

          <div className="absolute inset-0 bg-black/60"></div>

          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 z-10">
            <span className="text-amber-400 font-black text-xs uppercase tracking-widest mb-4 inline-block">
              “सेवा परमो धर्मः”
            </span>

            <h1
              id="objectives-heading"
              className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6 max-w-4xl"
            >
              Reducing Poverty, <br className="hidden md:block" />
              Sustaining <span className="text-amber-400">Dignity</span>
            </h1>

            <p className="text-white/90 text-base md:text-lg font-medium max-w-xl mx-auto leading-relaxed mb-10">
              Strengthening access to quality education, essential healthcare
              and nutritious food for tribal and extremely disadvantaged
              populations.
            </p>

            <Link
              to="/donate"
              className="bg-amber-400 text-[#0a4d3c] px-10 py-4 rounded-full font-black text-sm uppercase tracking-wider hover:bg-white transition shadow-lg"
              aria-label="Support SEVAA Foundation cause"
            >
              Support Our Cause
            </Link>

            <p className="sr-only">
              SEVAA Foundation objectives, poverty reduction NGO, tribal
              community NGO, education NGO, healthcare NGO, food security NGO,
              elderly support NGO, grassroots NGO India, सेवा परमो धर्मः,
              आदिवासी समुदाय सहायता, बच्चों की पढ़ाई, स्वास्थ्य सेवा, भोजन
              सहायता, गरीब परिवारों की मदद.
            </p>
          </div>
        </section>

        {/* 2. VISION & MISSION SECTION */}
        <section
          aria-labelledby="vision-mission-heading"
          className="bg-white py-20 mt-10"
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
            <div className="grid md:grid-cols-2 gap-8 lg:gap-16 items-center">
              {/* Left Side: Images Grid */}
              <div className="relative h-[400px] md:h-[500px] hidden md:block">
                <img
                  src="https://media.istockphoto.com/id/1389702301/photo/young-adult-woman-high-fives-child-volunteer.jpg?s=612x612&w=0&k=20&c=4-oqUiyBPSRpMIlNl2rpKWTqTg9skSe9PG1wS2GSJwY="
                  className="absolute top-0 left-0 w-3/4 h-[350px] object-cover rounded-3xl shadow-2xl z-10"
                  alt="SEVAA Foundation volunteers supporting tribal communities and rural families"
                  loading="lazy"
                />

                <img
                  src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=2070"
                  className="absolute bottom-0 right-0 w-2/3 h-[250px] object-cover rounded-3xl shadow-xl border-8 border-white z-20"
                  alt="Children education and support initiative by SEVAA Foundation NGO"
                  loading="lazy"
                />

                <div
                  className="absolute top-20 right-10 w-32 h-32 bg-amber-400 rounded-full mix-blend-multiply filter blur-2xl opacity-50 animate-pulse"
                  aria-hidden="true"
                ></div>
              </div>

              {/* Right Side: Content Cards */}
              <div className="space-y-8">
                <h2 id="vision-mission-heading" className="sr-only">
                  SEVAA Foundation Vision and Mission
                </h2>

                {/* Vision Card */}
                <article className="bg-white p-8 md:p-10 rounded-[2rem] shadow-[0_20px_50px_rgba(8,_112,_184,_0.07)] border border-slate-50 relative overflow-hidden group hover:-translate-y-2 transition-transform duration-300">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-teal-50 rounded-full -mr-16 -mt-16 transition-transform group-hover:scale-150 duration-500"></div>

                  <div className="relative z-10">
                    <p className="text-sm font-bold text-amber-500 uppercase tracking-widest mb-2">
                      Our Vision
                    </p>

                    <h3 className="text-3xl font-black text-[#0a4d3c] mb-4">
                      Empowering Futures
                    </h3>

                    <p className="text-slate-500 leading-relaxed font-medium">
                      We envision a society where every individual, especially
                      from tribal and vulnerable backgrounds, has the
                      opportunity to thrive. A world where education empowers
                      futures, healthcare preserves lives and food sustains
                      dignity.
                    </p>
                  </div>
                </article>

                {/* Mission Card */}
                <article className="bg-[#0a4d3c] p-8 md:p-10 rounded-[2rem] shadow-2xl relative overflow-hidden group text-white hover:-translate-y-2 transition-transform duration-300">
                  <div className="absolute bottom-0 right-0 w-32 h-32 bg-teal-800 rounded-full -mr-16 -mb-16 transition-transform group-hover:scale-150 duration-500"></div>

                  <div className="relative z-10">
                    <p className="text-sm font-bold text-amber-400 uppercase tracking-widest mb-2">
                      Our Mission
                    </p>

                    <h3 className="text-3xl font-black mb-4">
                      Grassroots Transformation
                    </h3>

                    <p className="text-slate-200 leading-relaxed font-medium">
                      To combat hunger, improve basic healthcare access and
                      enable education for children from economically vulnerable
                      families, building community-led solutions for long-term
                      self-reliance.
                    </p>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </section>

        {/* 3. CORE PILLARS & IMPACT SECTION */}
        <section
          aria-labelledby="core-pillars-heading"
          className="bg-[#f0f2f5] relative overflow-hidden py-16 md:py-32 mt-10 shadow-inner"
        >
          {/* Desktop Layout Background Circles */}
          <div className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 w-[450px] lg:w-[600px] h-[450px] lg:h-[600px] -translate-x-[40%] rounded-full overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.15)] z-0">
            <img
              src="https://thumbs.dreamstime.com/b/poor-street-children-india-15391453.jpg"
              className="w-full h-full object-cover"
              alt="SEVAA Foundation community support for poor children and vulnerable families"
              loading="lazy"
            />
          </div>

          <div className="hidden md:block absolute right-0 top-[5%] w-[250px] lg:w-[320px] h-[250px] lg:h-[320px] translate-x-[30%] rounded-full overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.15)] z-0">
            <img
              src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=2070"
              className="w-full h-full object-cover"
              alt="SEVAA Foundation education support for tribal and rural children"
              loading="lazy"
            />
          </div>

          <div className="hidden md:block absolute right-0 bottom-[5%] w-[250px] lg:w-[320px] h-[250px] lg:h-[320px] translate-x-[30%] rounded-full overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.15)] z-0">
            <img
              src="https://images.unsplash.com/photo-1584515933487-779824d29309?q=80&w=2070"
              className="w-full h-full object-cover"
              alt="SEVAA Foundation healthcare support and medical assistance for rural families"
              loading="lazy"
            />
          </div>

          <div className="max-w-7xl mx-auto px-6 relative z-10 flex flex-col md:flex-row items-center justify-center">
            {/* Mobile Fallback Layout */}
            <div className="md:hidden flex w-full justify-center gap-4 mb-8">
              <img
                src="https://images.unsplash.com/photo-1593113563332-61e860bc808e?q=80&w=2069"
                className="w-40 h-40 rounded-full object-cover shadow-lg"
                alt="SEVAA Foundation community support and donation work"
                loading="lazy"
              />

              <div className="flex flex-col gap-2">
                <img
                  src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=2070"
                  className="w-20 h-20 rounded-full object-cover shadow-lg"
                  alt="Education support for tribal children"
                  loading="lazy"
                />

                <img
                  src="https://images.unsplash.com/photo-1584515933487-779824d29309?q=80&w=2070"
                  className="w-20 h-20 rounded-full object-cover shadow-lg"
                  alt="Healthcare support for rural families"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="max-w-2xl text-center md:px-12 bg-white/40 md:bg-transparent p-6 rounded-3xl backdrop-blur-sm md:backdrop-blur-none border border-white/50 md:border-none">
              <h2 id="core-pillars-heading" className="sr-only">
                SEVAA Foundation Core Pillars and Impact
              </h2>

              <p className="text-slate-800 text-base md:text-xl lg:text-[22px] leading-relaxed md:leading-[1.8] font-medium tracking-wide">
                At SEVAA, we are committed to holistic development. From{" "}
                <span className="font-bold text-black">
                  supporting school access and learning resources
                </span>{" "}
                for children, to providing medical assistance and food support,
                our goal is comprehensive care. We proudly{" "}
                <span className="font-bold text-black">
                  support elderly individuals
                </span>{" "}
                with dignity and build community-led solutions that promote
                long-term resilience and self-reliance.
              </p>

              <p className="sr-only">
                SEVAA Foundation objectives, tribal education support,
                healthcare support, food security, elderly support, poverty
                reduction NGO, rural development NGO, grassroots social work,
                आदिवासी बच्चों की शिक्षा, स्वास्थ्य सेवा, भोजन सहायता,
                बुजुर्गों की सहायता, गरीब परिवारों की मदद.
              </p>
            </div>
          </div>
        </section>

        {/* 4. SEO KEYWORD TRUST TAGS */}
        {/* <section
          aria-labelledby="objectives-keywords-heading"
          className="py-14 px-6 bg-white"
        >
          <div className="max-w-7xl mx-auto">
            <h2 id="objectives-keywords-heading" className="sr-only">
              SEVAA Foundation Objectives and NGO Keywords
            </h2>

            <div className="flex flex-wrap justify-center gap-2">
              {seoKeywords.map((keyword) => (
                <span
                  key={keyword}
                  className="rounded-full bg-slate-50 border border-slate-200 px-3 py-1.5 text-[10px] md:text-xs font-bold text-slate-500 shadow-sm"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        </section> */}
      </main>
    </>
  );
};

export default Objectives;